<template>
    <div
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]"
        v-if="showBulkUpdateModal"
        aria-labelledby="bulk-update-modal"
        role="dialog"
        aria-modal="true"
        @click.self="closeModal()"
    >
        <div
            class="flex w-full max-w-[640px] mx-4 max-h-[90vh] flex-col rounded-lg bg-white dark:bg-slate-800 shadow-lg shadow-black/10"
            @click.stop
        >
            <div
                class="flex items-center justify-between p-6 pb-4 border-b border-slate-100 dark:border-slate-700"
            >
                <div>
                    <h2 class="text-slate-800 dark:text-slate-100 text-xl font-medium leading-7">
                        Update points balances
                    </h2>
                    <div
                        v-if="targetUserName"
                        class="mt-1 flex items-center gap-2"
                    >
                        <div
                            class="flex h-6 w-6 p-1 justify-center items-center rounded-full bg-slate-100 dark:bg-slate-700"
                        >
                            <Icon
                                icon="user"
                                class="w-4 h-4 text-slate-500 dark:text-slate-400"
                            />
                        </div>
                        <span class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5">{{ targetUserName }}</span>
                    </div>
                </div>
                <button
                    class="flex p-2 items-center justify-center rounded border-0 bg-transparent hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    @click="closeModal()"
                    type="button"
                    aria-label="Close modal"
                >
                    <Icon
                        icon="close"
                        :width="20"
                        :height="20"
                        class="text-slate-500 dark:text-slate-400"
                    />
                </button>
            </div>

            <div class="flex-1 overflow-y-auto px-6 py-4">
                <div
                    v-if="stalePrograms.length === 0"
                    class="text-center py-8 text-sm text-slate-500 dark:text-slate-400"
                >
                    No stale program balances found for this user.
                </div>

                <div v-else class="flex flex-col gap-4">
                    <div
                        v-for="(row, index) in rows"
                        :key="row.programUserId || row.manualRewardId"
                        class="flex flex-col gap-3 p-4 rounded-lg border border-slate-200 dark:border-slate-700"
                    >
                        <div class="flex items-center gap-3">
                            <div
                                class="flex w-20 h-10 p-1.5 px-3 justify-center items-center rounded-lg border border-slate-200 dark:border-slate-700"
                            >
                                <img
                                    v-if="row.image"
                                    :src="getProgramImageUrl(row.image)"
                                    :alt="row.name"
                                    class="max-w-full max-h-full object-contain"
                                />
                            </div>
                            <div class="flex-1">
                                <div
                                    class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                >
                                    {{ row.name }}
                                </div>
                                <div
                                    class="text-xs text-slate-500 dark:text-slate-400 mt-0.5"
                                >
                                    Last updated {{ row.lastUpdated }}
                                </div>
                            </div>
                        </div>

                        <div class="flex items-start gap-3">
                            <UiInput
                                :id="'points-' + index"
                                label="Points balance"
                                placeholder="Enter points"
                                v-model:inputValue="row.points"
                                input-format="thousands"
                                :options="{ wrapperClass: 'flex-1' }"
                            />
                            <div class="flex flex-col items-start flex-1">
                                <div
                                    class="flex pb-2 items-start gap-2.5 self-stretch"
                                >
                                    <span
                                        class="text-slate-600 dark:text-slate-400 text-sm font-medium leading-5"
                                    >
                                        Date
                                    </span>
                                </div>
                                <div
                                    class="flex items-center gap-2 self-stretch rounded border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 relative"
                                >
                                    <Icon
                                        icon="calendar"
                                        :width="16"
                                        :height="16"
                                        class="text-gray-400 absolute left-4 pointer-events-none z-10"
                                    />
                                    <flat-pickr
                                        :config="dateConfig"
                                        class="flex-1 text-slate-800 dark:text-slate-100 text-sm font-normal border-0 outline-0 bg-transparent py-1.5 pl-10 pr-4 w-full cursor-pointer placeholder:text-gray-400 dark:placeholder:text-slate-500"
                                        v-model="row.balanceDate"
                                        placeholder="Select date"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Transition name="fade">
                    <Errors :errors="errors" />
                </Transition>
            </div>

            <div
                class="flex justify-end items-center gap-2 p-6 pt-4 border-t border-slate-100 dark:border-slate-700"
            >
                <button
                    type="button"
                    class="btn-base btn-white"
                    @click="closeModal()"
                    :disabled="saving"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="btn-base btn-dark"
                    @click="submit"
                    :disabled="saving || stalePrograms.length === 0"
                >
                    <span v-if="saving" class="animate-spin">⟳</span>
                    <span v-else>Update all</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { usePointsStore } from '@/store/points';
import { notify } from 'notiwind';
import dayjs from 'dayjs';

import Errors from '@/components/errors.vue';
import FlatPickr from '@/components/ui/SafeFlatPickr.vue';
import Icon from '@/components/ui/icon/icon.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import { handleError } from '@/utils/error-handler';

const STALE_DAYS = 90;

export default {
    components: { Errors, FlatPickr, Icon, UiInput },
    data: () => ({
        rows: [],
        errors: [],
        saving: false,
        dateConfig: {
            dateFormat: 'm/d/Y',
            maxDate: 'today',
        },
    }),
    computed: {
        ...mapState(usePointsStore, [
            'rewards',
            'showBulkUpdateModal',
            'bulkUpdateUserId',
        ]),
        cdnUrl() {
            return window.cdnUrl;
        },
        targetUser() {
            if (!this.bulkUpdateUserId || !this.rewards?.users) return null;
            return (
                this.rewards.users.find(
                    (u) => u.id === Number(this.bulkUpdateUserId)
                ) || null
            );
        },
        targetUserName() {
            return this.targetUser?.name || '';
        },
        stalePrograms() {
            if (!this.targetUser) return [];
            const cutoff = dayjs().subtract(STALE_DAYS, 'day');
            return (this.targetUser.programs || []).filter((program) => {
                // Manual = not connected, OR connected but explicitly marked
                // as a manual rewards source.
                const isManual =
                    !program.connected ||
                    program.program_user?.rewards_source === 'manual';
                if (!isManual) return false;
                const updatedAt = program.updated_at;
                if (!updatedAt) return true;
                return dayjs(updatedAt).isBefore(cutoff);
            });
        },
    },
    watch: {
        showBulkUpdateModal(open) {
            if (open) {
                this.hydrateRows();
            } else {
                this.rows = [];
                this.errors = [];
            }
        },
    },
    methods: {
        ...mapActions(usePointsStore, [
            'getRewards',
            'closeBulkUpdateModal',
        ]),
        hydrateRows() {
            const today = dayjs().format('MM/DD/YYYY');
            this.rows = this.stalePrograms.map((program) => ({
                programUserId: program.program_user?.id || null,
                manualRewardId: program.manual_reward_id || null,
                programId:
                    program.program_id || program.program_user?.program_id,
                userId: this.bulkUpdateUserId,
                name: program.name,
                image: program.image || program.program_image,
                lastUpdated: program.updated_at
                    ? dayjs(program.updated_at).format('MM/DD/YYYY')
                    : 'unknown',
                points: program.points ?? '',
                balanceDate: today,
                source: program,
            }));
        },
        getProgramImageUrl(image) {
            if (!image) return '';
            if (typeof image === 'object' && image.path && image.file_name) {
                return `${this.cdnUrl}/${image.path}/_small/${image.file_name}`;
            }
            const cleanImage = String(image).replace(/^programs?_images\//, '');
            return `https://my10x.nyc3.cdn.digitaloceanspaces.com/uploads/program_images/_small/${cleanImage}`;
        },
        validate() {
            this.errors = [];
            for (const row of this.rows) {
                if (row.points === '' || row.points === null) {
                    this.errors.push({
                        message: `Enter a points balance for ${row.name}.`,
                    });
                }
                if (!row.balanceDate) {
                    this.errors.push({
                        message: `Select a date for ${row.name}.`,
                    });
                }
            }
            return this.errors.length === 0;
        },
        async submit() {
            if (!this.validate()) return;

            this.saving = true;

            const entries = this.rows.map((row) => ({
                user_id: row.userId,
                program_id: row.programId,
                points: row.points,
                balance_date: row.balanceDate,
                manual_reward_id: row.manualRewardId || null,
            }));

            try {
                await ApiService.apiCall('/rewards/bulk', 'post', {
                    entries,
                });

                notify({
                    group: 'success',
                    title: 'Balances updated',
                    text: `Updated ${this.rows.length} program balance${
                        this.rows.length === 1 ? '' : 's'
                    }.`,
                });
                await this.getRewards(true);
                this.closeModal();
            } catch (error) {
                handleError(error);
                notify({
                    group: 'error',
                    title: 'Update failed',
                    text: 'No balances were changed. Please try again.',
                });
            } finally {
                this.saving = false;
            }
        },
        closeModal() {
            this.errors = [];
            this.closeBulkUpdateModal();
        },
    },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
